import { toRef } from 'vue';

import type { Permission } from '@modules/permissions/utils';
import type { IUserLoginRequiredMfaCode, IUserSupervisionUnauthorizedErrorResponse, IUserUnauthorizedErrorResponse } from '@modules/user/types';
import {
  AccessDeniedError,
  RequestTimeoutError,
  ServerAccessDeniedError,
  ServerGatewayTimeoutError,
  ServerInternalError,
  ServerNotFoundError,
  ServerPayloadSizeError,
  ServerUnauthorizedError,
  ServerUnavailableError,
  ServerValidationError,
} from '@modules/core/errors';
import { getApiUrl } from '@modules/core/services/env-config/utils/envConfig';
import useMerchantIdStore from '@modules/merchant/store/useMerchantIdStore';
import { useCurrentNamespace } from '@modules/permissions/composables';
import { usePermissionsStore } from '@modules/permissions/store';
import { useXsrfTokenStore } from '@modules/tokens/store';
import {
  assert,
  isNullOrUndefined,
  isObject,
} from '@utils/guards';
import { promiseTimeout } from '@utils/promises';

import type {
  FetchOptions,
  OpenLinkOptions,
  RestOptions,
} from './types';
import {
  DEFAULT_TIMEOUT,
} from './constants';

type RequestBody = RequestInit['body'] | object | number | string | null | undefined;

export interface RestClientComposable {
  fetchGet<T>(url: string, options?: RestOptions): Promise<T>;
  fetchPost<T>(url: string, body: RequestBody, options?: RestOptions): Promise<T>;
  fetchPut<T>(url: string, body: RequestBody, options?: RestOptions): Promise<T>;
  fetchDelete<T>(url: string, options?: RestOptions): Promise<T>;
  openLink(url: string, options?: OpenLinkOptions): void;
}

const CONTENT_TYPE = 'Content-Type';

function handleValidationError(jsonData: { errors?: unknown } | undefined): void {
  let errors: unknown;

  errors = jsonData?.errors;

  if (!isObject(errors) || Object.keys(errors).length === 0) {
    errors = {};
  }

  throw new ServerValidationError(errors, jsonData ?? {});
}

function handleUnauthorizedError(jsonData: IUserUnauthorizedErrorResponse & IUserSupervisionUnauthorizedErrorResponse & IUserLoginRequiredMfaCode): void {
  if ((jsonData?.authorized === false && !jsonData?.errors && !jsonData?.required_mfa_code) || jsonData?.redirect_to_supervision === true) {
    throw new ServerUnauthorizedError(jsonData?.current_user, jsonData?.redirect_to_supervision === true);
  }
}

function prepareBody(body: RequestBody): RequestInit['body'] {
  if (body instanceof FormData || typeof body === 'string' || body instanceof Blob) {
    return body;
  }

  return JSON.stringify(body);
}

export default function useRestClient(): RestClientComposable {
  const merchantId = toRef(useMerchantIdStore(), 'merchantId');
  const restApiStore = useXsrfTokenStore();
  const xsrfToken = toRef(restApiStore, 'xsrfToken');
  const { namespace } = useCurrentNamespace();
  const { hasPermission } = usePermissionsStore();

  function checkPermissions(permission?: Permission | Permission[], message?: string): void {
    if (permission && !hasPermission(permission).value) {
      throw new AccessDeniedError(message);
    }
  }

  function afterFetch(response: Response): void {
    const responseXsrfToken = response.headers.get('xsrf-token');
    if (responseXsrfToken) {
      restApiStore.setXsrfToken(responseXsrfToken);
    }
  }

  function prepareFetchUrl(url: string): string {
    const urlObject = new URL(url);

    if (!urlObject.searchParams.has('merchant') && merchantId.value) {
      urlObject.searchParams.append('merchant', String(merchantId.value));
    }

    return decodeURIComponent(urlObject.toString());
  }

  function prepareFetchInit(init: RequestInit): RequestInit {
    const headers = new Headers(init.headers);

    if (xsrfToken.value) {
      headers.append('X-XSRF-TOKEN', xsrfToken.value);
    }
    headers.append('X-Frontend-Version', import.meta.env.PACKAGE_VERSION);

    if (!headers.has(CONTENT_TYPE) && !(init.body instanceof FormData)) {
      headers.append(CONTENT_TYPE, 'application/json');
    }

    return {
      ...init,
      headers,
    };
  }

  async function fetchData<T>(url: string, init: RequestInit, { timeout, permission, isEssential }: FetchOptions = {}): Promise<T> {
    const preparedUrl = prepareFetchUrl(url);
    const requestTimeout = timeout || DEFAULT_TIMEOUT;

    checkPermissions(permission, `No permissions to request: ${url}`);

    const inputSignal = init.signal;
    const abortController = new AbortController();
    const preparedInit = prepareFetchInit({
      ...init,
      signal: abortController.signal,
      credentials: 'include',
    });

    if (inputSignal) {
      inputSignal.addEventListener('abort', () => {
        abortController.abort(inputSignal.reason);
      });
    }

    const response = await promiseTimeout({
      promise: fetch(preparedUrl, preparedInit),
      error: () => new RequestTimeoutError(preparedUrl, requestTimeout, isEssential),
      promiseError: (originalError) => new ServerUnavailableError(originalError?.message, isEssential),
      onTimeout: () => {
        abortController.abort();
      },
      timeout: requestTimeout,
    });

    afterFetch(response);

    switch (response.status) {
      case 403:
        throw new ServerAccessDeniedError(undefined, isEssential);
      case 400: {
        let jsonData;
        try {
          jsonData = await response.json();
        } catch {}
        handleUnauthorizedError(jsonData);
        // some endpoints still might return 400 status on failed validation
        handleValidationError(jsonData);
        return jsonData as T;
      }
      case 404:
        throw new ServerNotFoundError(undefined, isEssential);
      case 422: {
        let jsonData;
        try {
          jsonData = await response.json();
        } catch {}

        handleValidationError(jsonData);
        break;
      }
      case 413:
        throw new ServerPayloadSizeError(undefined, isEssential);
      case 500:
        throw new ServerInternalError(undefined, isEssential);
      case 502:
      case 503:
        throw new ServerUnavailableError(undefined, isEssential);
      case 504:
        throw new ServerGatewayTimeoutError(undefined, isEssential);
      default:
        break;
    }

    return await response.json() as T;
  }

  function prepareUrl(
    url: string,
    queryParams: RestOptions['queryParams'],
    urlParams: RestOptions['urlParams'],
    excludeHost = false,
  ): string {
    let urlPath = url.replace(':namespace', namespace.value || 'anonym');
    if (urlParams) {
      for (const param of Object.keys(urlParams)) {
        urlPath = urlPath.replace(`:${param}`, !isNullOrUndefined(urlParams[param]) ? String(urlParams[param]) : '');
      }
    }

    const newUrl = new URL(`${excludeHost ? '' : getApiUrl()}${urlPath}`);
    if (queryParams) {
      assert(isObject(queryParams));
      for (const key of Object.keys(queryParams)) {
        newUrl.searchParams.append(key, String(queryParams[key]));
      }
    }

    return newUrl.toString();
  }

  function fetchGet<T>(url: string, {
    signal,
    queryParams,
    urlParams,
    ...fetchParams
  }: RestOptions = {}): Promise<T> {
    return fetchData<T>(prepareUrl(url, queryParams, urlParams), {
      method: 'GET',
      signal,
    }, fetchParams);
  }

  function fetchPost<T>(
    url: string,
    body: RequestBody,
    {
      signal,
      queryParams,
      urlParams,
      ...fetchParams
    }: RestOptions = {},
  ): Promise<T> {
    return fetchData<T>(prepareUrl(url, queryParams, urlParams), {
      method: 'POST',
      body: prepareBody(body),
      signal,
    }, fetchParams);
  }

  function fetchPut<T>(
    url: string,
    body: RequestBody,
    {
      signal,
      queryParams,
      urlParams,
      ...fetchParams
    }: RestOptions = {},
  ): Promise<T> {
    return fetchData<T>(prepareUrl(url, queryParams, urlParams), {
      method: 'PUT',
      body: prepareBody(body),
      signal,
    }, fetchParams);
  }

  function fetchDelete<T>(url: string, {
    signal,
    queryParams,
    urlParams,
    ...fetchParams
  }: RestOptions = {}): Promise<T> {
    return fetchData<T>(prepareUrl(url, queryParams, urlParams), {
      method: 'DELETE',
      signal,
    }, fetchParams);
  }

  function openLink(url: string, {
    permission,
    queryParams,
    urlParams,
    isNewWindow,
    excludeHost,
  }: OpenLinkOptions = {}): void {
    checkPermissions(permission);

    const urlObject = new URL(prepareUrl(url, queryParams, urlParams, excludeHost));
    if (!urlObject.searchParams.has('authenticity_token')) {
      urlObject.searchParams.append('authenticity_token', xsrfToken.value);
    }

    window.open(urlObject.toString(), isNewWindow ? '_blank' : '_self');
  }

  return {
    fetchGet,
    fetchPost,
    fetchPut,
    fetchDelete,
    openLink,
  };
}
